/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components and global components for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue';

/**
 * Import async components.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'athletes-content-slider': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.athletes-content-slider' */ '~async/athletes-content-slider/athletes-content-slider'
        ),
    }),
    'bought-together': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.bought-together' */ '~async/bought-together/bought-together'
        ),
    }),
    'call-to-action': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.call-to-action' */ '~async/call-to-action/call-to-action'
        ),
    }),
    'callout-product-card': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.callout-product-card' */ '~async/callout-product-card/callout-product-card'
        ),
    }),
    'calorie-calculator': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.calorie-calculator' */ '~async/calorie-calculator/calorie-calculator'
        ),
    }),
    'campaign-product-list': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.campaign-product-list' */ '~async/campaign-product-list/campaign-product-list'
        ),
    }),
    'cart-drawer': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.cart-drawer' */ '~async/cart-drawer/cart-drawer'
        ),
    }),
    'category-linklist': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.category-linklist' */ '~async/category-linklist/category-linklist'
        ),
    }),
    'category-linklist-section': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.category-linklist-section' */ '~async/category-linklist-section/category-linklist-section'
        ),
    }),
    'collection-header': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.collection-header' */ '~async/collection-header/collection-header'
        ),
    }),
    'community-grid': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.community-grid' */ '~async/community-grid/community-grid'
        ),
    }),
    'content-card': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.content-card' */ '~async/content-card/content-card'
        ),
    }),
    'content-card-slider': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.content-card-slider' */ '~async/content-card-slider/content-card-slider'
        ),
    }),
    'content-grid': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.content-grid' */ '~async/content-grid/content-grid'
        ),
    }),
    'content-slider': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.content-slider' */ '~async/content-slider/content-slider'
        ),
    }),
    'count-down': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.count-down' */ '~async/count-down/count-down'
        ),
    }),
    'custom-variant-collection': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.custom-variant-collection' */ '~async/custom-variant-collection/custom-variant-collection'
        ),
    }),
    'expert-carousel': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.expert-carousel' */ '~async/expert-carousel/expert-carousel'
        ),
    }),
    'faq-section': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.faq-section' */ '~async/faq-section/faq-section'
        ),
    }),
    'featured-category-large': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.featured-category-large' */ '~async/featured-category-large/featured-category-large'
        ),
    }),
    'featured-category-small': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.featured-category-small' */ '~async/featured-category-small/featured-category-small'
        ),
    }),
    'footer-newsletter-signup': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.footer-newsletter-signup' */ '~async/footer-newsletter-signup/footer-newsletter-signup'
        ),
    }),
    'fullwidth-teaser': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.fullwidth-teaser' */ '~async/fullwidth-teaser/fullwidth-teaser'
        ),
    }),
    'goals-teaser': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.goals-teaser' */ '~async/goals-teaser/goals-teaser'
        ),
    }),
    'icon-text': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.icon-text' */ '~async/icon-text/icon-text'
        ),
    }),
    'inspirational-carousel': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.inspirational-carousel' */ '~async/inspirational-carousel/inspirational-carousel'
        ),
    }),
    'last-order-tracking': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.last-order-tracking' */ '~async/last-order-tracking/last-order-tracking'
        ),
    }),
    'main-404': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-404' */ '~async/main-404/main-404'
        ),
    }),
    'main-blog': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-blog' */ '~async/main-blog/main-blog'
        ),
    }),
    'main-cart': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-cart' */ '~async/main-cart/main-cart'
        ),
    }),
    'main-collection': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-collection' */ '~async/main-collection/main-collection'
        ),
    }),
    'main-list-collections': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-list-collections' */ '~async/main-list-collections/main-list-collections'
        ),
    }),
    'main-page': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-page' */ '~async/main-page/main-page'
        ),
    }),
    'main-product': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-product' */ '~async/main-product/main-product'
        ),
    }),
    'main-search': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.main-search' */ '~async/main-search/main-search'
        ),
    }),
    'market-selector': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.market-selector' */ '~async/market-selector/market-selector'
        ),
    }),
    'newsletter-section': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.newsletter-section' */ '~async/newsletter-section/newsletter-section'
        ),
    }),
    'plan-accordion': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.plan-accordion' */ '~async/plan/plan-accordion/plan-accordion'
        ),
    }),
    'plan-intro': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.plan-intro' */ '~async/plan/plan-intro/plan-intro'
        ),
    }),
    'plans-popup': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.plans-popup' */ '~async/plan/plans-popup/plans-popup'
        ),
    }),
    'plans-toc': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.plan-toc' */ '~async/plan/plan-toc/plan-toc'
        ),
    }),
    'product-carousel': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.product-carousel' */ '~async/product-carousel/product-carousel'
        ),
    }),
    'product-grid': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.product-grid' */ '~async/product-grid/product-grid'
        ),
    }),
    'product-information': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.product-information' */ '~async/product-information/product-information'
        ),
    }),
    'product-sources': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.product-sources' */ '~async/product-sources/product-sources'
        ),
    }),
    'promo-banner': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.promo-banner' */ '~async/promo-banner/promo-banner'
        ),
    }),
    'quick-add': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.quick-add' */ '~async/quick-add/quick-add'
        ),
    }),
    'reasons-why': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.reasons-why' */ '~async/reasons-why/reasons-why'
        ),
    }),
    'recipe-page': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.recipe-page' */ '~async/recipe-page/recipe-page'
        ),
    }),
    'rich-text': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.rich-text' */ '~async/rich-text/rich-text'
        ),
    }),
    'scroll-to-top-button': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.scroll-to-top-button' */ '~async/scroll-to-top-button/scroll-to-top-button'
        ),
    }),
    'search-bar': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.search-bar' */ '~async/search-bar/search-bar'
        ),
    }),
    'site-footer': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.site-footer' */ '~async/site-footer-group/site-footer/site-footer'
        ),
    }),
    'split-section': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.split-section' */ '~async/split-section/split-section'
        ),
    }),
    'stats-grid': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.stats-grid' */ '~async/stats-grid/stats-grid'
        ),
    }),
    'typeform-embed': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.typeform-embed' */ '~async/typeform-embed/typeform-embed'
        ),
    }),
    'usp-grid': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.usp-grid' */ '~async/usp-grid/usp-grid'
        ),
    }),
    'video-slider': defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.video-slider' */ '~async/video-slider/video-slider'
        ),
    }),
    // canvas-async-object-end
  };
}

/**
 * Import global components.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility';
import ArtDirection from '~global/images/art-direction';
import Btn from '~global/btn/btn';
import DiscountCampaigns from '~global/discount-campaigns/discount-campaigns';
import Index from '~global/core/index';
import Navigation from '~global/site-header-group/navigation/navigation';
import Overlay from '~global/overlay/overlay';
import ProductCard from '~global/product-card/product-card';
import ProductPrices from '~global/product-prices/product-prices';
import ResponsiveImage from '~global/images/responsive-image';
import SiteHeader from '~global/site-header-group/site-header/site-header';
import SiteHeaderGroup from '~global/site-header-group/site-header-group';
import ToastOverlay from '~global/toast-overlay/toast-overlay';
import TransitionHeight from '~global/transition-height/transition-height';
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'art-direction': ArtDirection,
    'discount-campaigns': DiscountCampaigns,
    'product-card': ProductCard,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    'toast-overlay': ToastOverlay,
    'transition-height': TransitionHeight,
    accessibility: Accessibility,
    btn: Btn,
    index: Index,
    navigation: Navigation,
    overlay: Overlay,
    // canvas-global-object-end
  };
}

/**
 * Import web components.
 * - Must be manually updated with new async web components (components/async).
 * - Use component command import to update.
 */
// canvas-web-import-start
import(/* webpackChunkName: 'web.blog-text' */ '~async/blog-text/blog-text');
import(
  /* webpackChunkName: 'web.campaign-teaser' */ '~async/campaign-teaser/campaign-teaser'
);
import(
  /* webpackChunkName: 'web.content-breadcrumb' */ '~web/content-breadcrumb/content-breadcrumb'
);
import(
  /* webpackChunkName: 'web.review-list' */ '~web/review-list/review-list'
);
import(
  /* webpackChunkName: 'web.payment-icons' */ '~web/payment-icons/payment-icons'
);
import(/* webpackChunkName: 'web.flip-clock' */ '~async/flip-clock/flip-clock');
import(
  /* webpackChunkName: 'web.grid-teaser' */ '~async/grid-teaser/grid-teaser'
);
import(
  /* webpackChunkName: 'web.hero-teaser' */ '~async/hero-teaser/hero-teaser'
);
import(
  /* webpackChunkName: 'web.influencer-hero-teaser' */ '~async/influencer-hero-teaser/influencer-hero-teaser'
);
import(
  /* webpackChunkName: 'web.main-article' */ '~async/main-article/main-article'
);
import(
  /* webpackChunkName: 'web.newsletter-popup' */ '~async/newsletter-popup/newsletter-popup'
);
import(
  /* webpackChunkName: 'web.newsletter-signup' */ '~async/newsletter-signup/newsletter-signup'
);
import(
  /* webpackChunkName: 'web.order-tracking' */ '~async/order-tracking/order-tracking'
);
import(
  /* webpackChunkName: 'web.plans-download' */ '~async/plan/plans-download/plans-download'
);
import(
  /* webpackChunkName: 'web.plans-popup' */ '~async/plan/plans-popup/plans-popup'
);
import(
  /* webpackChunkName: 'web.simple-teaser' */ '~async/simple-teaser/simple-teaser'
);
import(
  /* webpackChunkName: 'web.video-player-web' */ '~async/video-player/video-player-web'
);
import(
  /* webpackChunkName: 'web.main-navigation' */ '~web/main-navigation/main-navigation'
);
// canvas-web-import-end
